import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Image as ImageIcon, Info, Eye, Trash2 } from "lucide-react";

import { getAccount } from "../../../api/account";
import { uploadImage } from "../../../api/account";
import SearchSelect, { SearchSelectProps } from "../../UI/search_select/search_select";
import { Config } from "../../../utils";
import { resolveImageSrc } from "../../../utils/image-src";
import { Account } from "../../../interfaces/account";
import { Category } from "../../../interfaces/category";
import { Supplier } from "../../../interfaces/supplier";
import {
    FabricsOrLiningsItemDetailsFields,
    FabricsOrLiningsItemDetailsFieldsAction,
} from "../../../redux/modules/my-shop/fabrics-or-linings-item-details-fields-reducer";
import {
    FormFields,
    FormFieldsAction,
} from "../../../redux/modules/my-shop/form-fields-reducer";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import UploadErrorBanner from "../../UI/upload-error-banner";
import Checkbox from "../../../ui_components/checkbox/checkbox";
import { buildItemDescriptionsFromSettings } from "../../../utils/description-settings";

export interface MyShopSheetValidationState {
    category: boolean;
    supplier: boolean;
    name: boolean;
}

interface MyShopItemWideSheetContentProps {
    categories: Category[];
    suppliers: Supplier[];
    descriptionSettings?: {
        orderingDescription?: any[] | null;
        salesDescriptionCom?: any[] | null;
        salesDescriptionWorkroom?: any[] | null;
    };
    formFields: FormFields;
    fabricsOrLiningsItemDetailsFields: FabricsOrLiningsItemDetailsFields;
    dispatch: Dispatch<FormFieldsAction>;
    dispatchItemDetails: Dispatch<FabricsOrLiningsItemDetailsFieldsAction>;
    validationErrors: MyShopSheetValidationState;
    setValidationErrors: Dispatch<SetStateAction<MyShopSheetValidationState>>;
    isFabricsOrLiningsCategory: boolean;
    isEditMode: boolean;
    readOnlyPricing?: boolean;
    hideSubtype?: boolean;
    lockCategory?: boolean;
    submitErrorMessage?: string;
    onOpenSupplierModal?(): void;
    onDescriptionSettingsClick?(): void;
    canEditMyShop?: boolean;
    prefill?: any;
    lockPrefill?: boolean;
    noSupplierRequired?: boolean;
}

const fieldClassName =
    "h-11 rounded-md border border-input bg-background text-[14px] font-normal text-[#111827] placeholder:text-[#94A3B8]";

type DescriptionSettingItem = {
    id: number;
    label: string;
    value: string;
    parentheses?: string;
};

type DescriptionSettingsState = {
    orderingDescription: DescriptionSettingItem[];
    salesDescriptionCom: DescriptionSettingItem[];
    salesDescriptionWorkroom: DescriptionSettingItem[];
};

const emptyDescriptionSettings: DescriptionSettingsState = {
    orderingDescription: [],
    salesDescriptionCom: [],
    salesDescriptionWorkroom: [],
};

const getScopedAccountId = (): number => {
    if (typeof window === "undefined") {
        return 0;
    }
    return Number(
        localStorage.getItem("ownerAccountId") ||
            localStorage.getItem("accountId") ||
            "0"
    );
};

const normalizeDescriptionSettings = (
    account?: Account | null
): DescriptionSettingsState => ({
    orderingDescription: Array.isArray(account?.orderingDescription)
        ? (account?.orderingDescription as DescriptionSettingItem[])
        : [],
    salesDescriptionCom: Array.isArray(account?.salesDescriptionCom)
        ? (account?.salesDescriptionCom as DescriptionSettingItem[])
        : [],
    salesDescriptionWorkroom: Array.isArray(account?.salesDescriptionWorkroom)
        ? (account?.salesDescriptionWorkroom as DescriptionSettingItem[])
        : [],
});

const sanitizeDecimalInput = (
    value: string,
    maxIntegerDigits = 5,
    maxDecimalDigits = 2
): string => {
    let nextValue = value.replace(/[^\d.]/g, "");

    if (nextValue.startsWith(".")) {
        nextValue = `0${nextValue}`;
    }

    const firstDotIndex = nextValue.indexOf(".");
    if (firstDotIndex >= 0) {
        nextValue =
            nextValue.slice(0, firstDotIndex + 1) +
            nextValue.slice(firstDotIndex + 1).replace(/\./g, "");
    }

    const [integerPart = "", decimalPart = ""] = nextValue.split(".");
    const trimmedIntegerPart = integerPart.slice(0, maxIntegerDigits);
    const trimmedDecimalPart = decimalPart.slice(0, maxDecimalDigits);

    if (nextValue.includes(".")) {
        return `${trimmedIntegerPart}.${trimmedDecimalPart}`;
    }

    return trimmedIntegerPart;
};

const formatCurrency = (value: string | number | undefined | null): string => {
    const parsedValue = Number(value || 0);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
        return "$0.00";
    }
    return `$${parsedValue.toFixed(2)}`;
};

const formatDescriptionPreviewLine = (value: string): string => {
    if (!value) {
        return "";
    }

    const parts = value
        .split(/\r?\n+/)
        .map((part) => part.trim())
        .filter(Boolean);

    return parts.join(" - ");
};

const formatDecimalOnBlur = (
    value: string | number | undefined | null,
    maxIntegerDigits = 5,
    maxDecimalDigits = 2
): string => {
    if (value === null || value === undefined) {
        return "";
    }

    const sanitizedValue = sanitizeDecimalInput(
        String(value).trim(),
        maxIntegerDigits,
        maxDecimalDigits
    );

    if (!sanitizedValue) {
        return "";
    }

    const parsedValue = Number(sanitizedValue);
    if (!Number.isFinite(parsedValue)) {
        return "";
    }

    return parsedValue.toFixed(maxDecimalDigits);
};

const toInputValue = (
    value: string | number | undefined | null
): string => {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const getItemTypeIdForCategory = (
    categoryId: number,
    categories: Category[]
): number => {
    const selectedCategory = categories.find((category) => category.id === categoryId);
    const rootCategory = selectedCategory?.parentCategoryId
        ? categories.find((category) => category.id === selectedCategory.parentCategoryId)
        : selectedCategory;

    return rootCategory?.name === "Fees" ? 1 : 2;
};

const getImageSrc = (avatarId?: string | null): string => {
    return resolveImageSrc(avatarId);
};

const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <section className="rounded-[10px] bg-[#F8FAFC] px-4 py-4 md:px-5 md:py-5">
        <h3 className="m-0 text-[16px] font-medium text-[#111827]">{title}</h3>
        <div className="mt-4 space-y-4">{children}</div>
    </section>
);


const ErrorText = ({ children }: { children: React.ReactNode }) => (
    <p className="m-0 mt-1 text-[12px] text-[#DC2626]">{children}</p>
);

const PreviewBlock = ({
    label,
    value,
}: {
    label: string;
    value: string;
}) => {
    const previewValue = formatDescriptionPreviewLine(value);

    return (
        <div className="space-y-1">
            <div className="text-[13px] font-medium text-[#60778F]">{label}</div>
            <div className="text-[14px] text-[#111827]">{previewValue || "—"}</div>
        </div>
    );
};

const MyShopItemWideSheetContent = ({
    categories,
    suppliers,
    descriptionSettings: providedDescriptionSettings,
    formFields,
    fabricsOrLiningsItemDetailsFields,
    dispatch,
    dispatchItemDetails,
    validationErrors,
    setValidationErrors,
    isFabricsOrLiningsCategory,
    isEditMode,
    readOnlyPricing = false,
    hideSubtype = false,
    lockCategory = false,
    submitErrorMessage,
    onOpenSupplierModal,
    onDescriptionSettingsClick,
    canEditMyShop = true,
    prefill = null,
    lockPrefill = false,
    noSupplierRequired = false,
}: MyShopItemWideSheetContentProps): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const pricingMode: "markup" | "specify" =
        formFields.retailPricingMethod === "rateRetail" ||
        formFields.tradePricingMethod === "rateTrade"
            ? "specify"
            : "markup";
    const [shippingHandlingEnabled, setShippingHandlingEnabled] =
        useState<boolean>(false);
    const [shippingHandlingMode, setShippingHandlingMode] = useState<
        "per-yard" | "per-item"
    >("per-yard");
    const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
    const [imageUploadError, setImageUploadError] = useState<string>("");
    const [showCustomerFacingName, setShowCustomerFacingName] = useState<boolean>(false);
    const [localCategories, setLocalCategories] = useState<Category[]>(categories);
    const [localSuppliers, setLocalSuppliers] = useState<Supplier[]>(suppliers);
    const [descriptionSettings, setDescriptionSettings] = useState<DescriptionSettingsState>(
        emptyDescriptionSettings
    );

    useEffect(() => {
        setLocalCategories(categories);
    }, [categories]);

    useEffect(() => {
        setLocalSuppliers(suppliers);
    }, [suppliers]);

    useEffect(() => {
        if (!providedDescriptionSettings) {
            return;
        }

        setDescriptionSettings({
            orderingDescription: Array.isArray(providedDescriptionSettings.orderingDescription)
                ? (providedDescriptionSettings.orderingDescription as DescriptionSettingItem[])
                : [],
            salesDescriptionCom: Array.isArray(providedDescriptionSettings.salesDescriptionCom)
                ? (providedDescriptionSettings.salesDescriptionCom as DescriptionSettingItem[])
                : [],
            salesDescriptionWorkroom: Array.isArray(
                providedDescriptionSettings.salesDescriptionWorkroom
            )
                ? (providedDescriptionSettings.salesDescriptionWorkroom as DescriptionSettingItem[])
                : [],
        });
    }, [providedDescriptionSettings]);

    useEffect(() => {
        if (providedDescriptionSettings) {
            return;
        }

        let isMounted = true;

        const loadDescriptionSettings = async () => {
            const accountId = getScopedAccountId();
            if (!accountId) {
                return;
            }

            try {
                const account = await getAccount(accountId);
                if (!isMounted) {
                    return;
                }

                setDescriptionSettings(normalizeDescriptionSettings(account));
            } catch (error) {
                if (isMounted) {
                    setDescriptionSettings(emptyDescriptionSettings);
                }
            }
        };

        loadDescriptionSettings();

        return () => {
            isMounted = false;
        };
    }, [providedDescriptionSettings]);

    useEffect(() => {
        if (formFields.customerFacingName?.trim()) {
            setShowCustomerFacingName(true);
        }
    }, [formFields.customerFacingName]);


    useEffect(() => {
        if (readOnlyPricing) {
            return;
        }

        const hasPricingInput =
            String(formFields.cost || "").length > 0 ||
            String(formFields.retailPricingValue || "").length > 0 ||
            String(formFields.tradePricingValue || "").length > 0;

        if (!hasPricingInput) {
            return;
        }

        if (
            pricingMode === "markup" &&
            (formFields.retailPricingMethod !== "markUpCostRetail" ||
                formFields.tradePricingMethod !== "markUpCostTrade")
        ) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    retailPricingMethod: "markUpCostRetail",
                    tradePricingMethod: "markUpCostTrade",
                },
            });
            return;
        }

        if (
            pricingMode === "specify" &&
            (formFields.retailPricingMethod !== "rateRetail" ||
                formFields.tradePricingMethod !== "rateTrade")
        ) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    retailPricingMethod: "rateRetail",
                    tradePricingMethod: "rateTrade",
                },
            });
        }
    }, [
        dispatch,
        formFields.cost,
        formFields.retailPricingMethod,
        formFields.retailPricingValue,
        formFields.tradePricingMethod,
        formFields.tradePricingValue,
        pricingMode,
        readOnlyPricing,
    ]);



    useEffect(() => {
        const hasMappedSihCostType = ["cost-per-yard", "cost-per-fabric"].includes(
            formFields.sihCostType || ""
        );

        if (hasMappedSihCostType) {
            return;
        }

        if (formFields.handlingFeeMethod === "rate" && formFields.handlingFeeValue) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    sihCostType: "cost-per-fabric",
                    shippingCost: formFields.handlingFeeValue,
                },
            });
            return;
        }

        if (formFields.shippingCost) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    sihCostType: "cost-per-yard",
                },
            });
        }
    }, [
        dispatch,
        formFields.handlingFeeMethod,
        formFields.handlingFeeValue,
        formFields.sihCostType,
        formFields.shippingCost,
    ]);

    useEffect(() => {
        const hasShippingOrHandling =
            !!formFields.shippingCost ||
            !!formFields.sihCostType ||
            !!formFields.handlingFeeValue ||
            !!formFields.handlingFeeMethod;

        setShippingHandlingEnabled((current) => current || hasShippingOrHandling);

        if (formFields.sihCostType === "cost-per-fabric") {
            setShippingHandlingMode("per-item");
            return;
        }

        if (formFields.sihCostType === "cost-per-yard") {
            setShippingHandlingMode("per-yard");
            return;
        }

        if (formFields.handlingFeeMethod === "rate") {
            setShippingHandlingMode("per-item");
        } else {
            setShippingHandlingMode("per-yard");
        }
    }, [
        formFields.shippingCost,
        formFields.sihCostType,
        formFields.handlingFeeMethod,
        formFields.handlingFeeValue,
    ]);

    useEffect(() => {
        const {
            salesDescription,
            salesDescriptionWorkroom,
            orderingDescription,
        } = buildItemDescriptionsFromSettings({
            descriptionSettings,
            formFields,
            detailsFields: fabricsOrLiningsItemDetailsFields,
            isFabricsOrLiningsCategory,
        });

        if (
            salesDescription !== formFields.salesDescription ||
            salesDescriptionWorkroom !== formFields.salesDescriptionWorkroom ||
            orderingDescription !== formFields.orderingDescription
        ) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    salesDescription,
                    salesDescriptionWorkroom,
                    orderingDescription,
                },
            });
        }
    }, [
        descriptionSettings,
        dispatch,
        formFields.customerFacingName,
        formFields.itemNumber,
        formFields.name,
        formFields.orderingDescription,
        formFields.salesDescription,
        formFields.salesDescriptionWorkroom,
        formFields.supplierName,
        descriptionSettings,
        fabricsOrLiningsItemDetailsFields,
        isFabricsOrLiningsCategory,
    ]);

    const selectedRootCategoryId = formFields.categoryId || 0;
    const selectedRootCategory = localCategories.find(
        (category) => category.id === selectedRootCategoryId
    );
    const selectedSubcategory = formFields.subcategoryId
        ? localCategories.find((category) => category.id === formFields.subcategoryId)
        : null;
    const subcategories = localCategories.filter(
        (category) =>
            category.parentCategoryId === selectedRootCategoryId && category.isActive
    );
    const shouldShowSubcategory = !hideSubtype && subcategories.length > 0;

    const clearValidationError = (field: keyof MyShopSheetValidationState) => {
        setValidationErrors((current) => ({ ...current, [field]: false }));
    };

    const handleTextFieldChange = (
        field: keyof FormFields,
        value: string | number | boolean | null
    ) => {
        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                [field]: value,
            },
        });
    };

    const handleRootCategorySelect = (id: number, name: string) => {
        if (!id) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    categoryId: 0,
                    subcategoryId: null,
                    categoryName: "",
                    parentCategoryId: undefined,
                    itemTypeId: 0,
                },
            });
            return;
        }

        const selectedCategory = localCategories.find((category) => category.id === id);
        let noSupplierRequired = selectedCategory?.id === 102 || selectedCategory?.name?.toLowerCase() === 'fees';

        clearValidationError("category");
        if (noSupplierRequired) {
          clearValidationError("supplier");
        }
        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                categoryId: id,
                subcategoryId: null,
                categoryName: selectedCategory?.name || name,
                parentCategoryId: undefined,
                itemTypeId: getItemTypeIdForCategory(id, localCategories),
            },
        });
    };

    const handleSubcategorySelect = (id: number, _name: string) => {
        if (!id) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    subcategoryId: null,
                    parentCategoryId: undefined,
                },
            });
            return;
        }

        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                categoryId: selectedRootCategoryId,
                subcategoryId: id,
                categoryName: selectedRootCategory?.name || "",
                parentCategoryId: selectedRootCategoryId,
                itemTypeId: getItemTypeIdForCategory(
                    selectedRootCategoryId,
                    localCategories
                ),
            },
        });
    };

    const handleSupplierSelect = (id: number, name: string) => {
        const normalizedSupplierId = id > 0 ? id : null;

        if (normalizedSupplierId) {
            clearValidationError("supplier");
        }

        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                supplierId: normalizedSupplierId,
                supplierName: normalizedSupplierId ? name : "",
            },
        });
    };

    const handleCategorySearch: SearchSelectProps["onSearch"] = async (term) => {
        const rootCategories = localCategories
            .filter((category) => category.parentCategoryId === null && category.isActive)
            .filter((category) =>
                term
                    ? category.name.toLowerCase().includes(term.toLowerCase())
                    : true
            )
            .sort((a, b) => a.name.localeCompare(b.name));

        return rootCategories.map((category) => ({
            id: category.id,
            displayName: category.name,
        }));
    };

    const handleSubcategorySearch: SearchSelectProps["onSearch"] = async (term) => {
        return subcategories
            .filter((category) =>
                term
                    ? category.name.toLowerCase().includes(term.toLowerCase())
                    : true
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((category) => ({
                id: category.id,
                displayName: category.name,
            }));
    };

    const handleSupplierSearch: SearchSelectProps["onSearch"] = async (term) => {
        const filteredSuppliers = localSuppliers
            .filter((supplier) => supplier.active)
            .filter((supplier) =>
                term
                    ? supplier.displayName.toLowerCase().includes(term.toLowerCase())
                    : true
            )
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map((supplier) => ({
                id: supplier.id,
                displayName: supplier.displayName,
            }));

        return filteredSuppliers;
    };

    const handlePricingModeChange = (mode: "markup" | "specify") => {
        if (mode === "markup") {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    cost: "",
                    retailPricingMethod: "markUpCostRetail",
                    tradePricingMethod: "markUpCostTrade",
                    retailPricingValue: "",
                    tradePricingValue: "",
                },
            });
            return;
        }

        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                cost: "",
                retailPricingMethod: "rateRetail",
                tradePricingMethod: "rateTrade",
                retailPricingValue: "",
                tradePricingValue: "",
            },
        });
    };

    const handlePricingFieldChange =
        (field: keyof FormFields, options?: { maxIntegerDigits?: number }) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    [field]: sanitizeDecimalInput(
                        event.target.value,
                        options?.maxIntegerDigits
                    ),
                },
            });
        };

    const handlePricingFieldBlur =
        (field: keyof FormFields, options?: { maxIntegerDigits?: number }) => () => {
            const formattedValue = formatDecimalOnBlur(
                formFields[field] as string | number | null | undefined,
                options?.maxIntegerDigits
            );

            if (String(formFields[field] || "") === formattedValue) {
                return;
            }

            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    [field]: formattedValue,
                },
            });
        };

    const handleSpecificationChange =
        (field: keyof FabricsOrLiningsItemDetailsFields) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatchItemDetails({
                type: "updateFormFields",
                formFieldUpdates: {
                    [field]: sanitizeDecimalInput(event.target.value, 3),
                },
            });
        };

    const handleSpecificationBlur =
        (field: keyof FabricsOrLiningsItemDetailsFields) => () => {
            const formattedValue = formatDecimalOnBlur(
                fabricsOrLiningsItemDetailsFields[field] as
                    | string
                    | number
                    | null
                    | undefined,
                3
            );

            if (
                String(fabricsOrLiningsItemDetailsFields[field] || "") ===
                formattedValue
            ) {
                return;
            }

            dispatchItemDetails({
                type: "updateFormFields",
                formFieldUpdates: {
                    [field]: formattedValue,
                },
            });
        };

    const toggleSpecificationCheckbox =
        (field: keyof FabricsOrLiningsItemDetailsFields) => () => {
            dispatchItemDetails({
                type: "updateFormFields",
                formFieldUpdates: {
                    [field]: !fabricsOrLiningsItemDetailsFields[field],
                },
            });
        };

    const handleShippingToggle = () => {
        const nextValue = !shippingHandlingEnabled;
        setShippingHandlingEnabled(nextValue);

        if (!nextValue) {
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    shippingCost: "",
                    sihCostType: "",
                    handlingFeeMethod: "",
                    handlingFeeValue: "",
                },
            });
            return;
        }

        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                sihCostType:
                    shippingHandlingMode === "per-item"
                        ? "cost-per-fabric"
                        : "cost-per-yard",
                handlingFeeMethod: "",
                handlingFeeValue: "",
            },
        });
    };

    const handleShippingModeChange = (mode: "per-yard" | "per-item") => {
        setShippingHandlingMode(mode);
        dispatch({
            type: "updateFormFields",
            formFieldUpdates: {
                sihCostType: mode === "per-yard" ? "cost-per-yard" : "cost-per-fabric",
                handlingFeeMethod: "",
                handlingFeeValue: "",
            },
        });
    };

    const handleImageUpload = async (file: File) => {
        setImageUploadError("");

        if (file.size > 10 * 1024 * 1024) {
            setImageUploadError("Max allowed size is 10MB.");
            return;
        }

        setIsUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("accountId", String(getScopedAccountId()));
            formData.append("fileStoreUrl", Config.fileStoreUrl);
            const response = await uploadImage(formData);
            dispatch({
                type: "updateFormFields",
                formFieldUpdates: {
                    avatarId: response.url,
                },
            });
        } catch (error) {
            setImageUploadError(
                error instanceof Error && error.message
                    ? error.message
                    : "Failed to upload image. Please try again."
            );
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleImageFileChange = async (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        if (!canEditMyShop) return
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        await handleImageUpload(file);
        event.target.value = "";
    };

    const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
        if (!canEditMyShop) return
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (!file) {
            return;
        }
        await handleImageUpload(file);
    };

    const computedRetailPrice =
        pricingMode === "markup"
            ? Number(formFields.cost || 0) *
              (1 + Number(formFields.retailPricingValue || 0) / 100)
            : Number(formFields.retailPricingValue || 0);
    const computedTradePrice =
        pricingMode === "markup"
            ? Number(formFields.cost || 0) *
              (1 + Number(formFields.tradePricingValue || 0) / 100)
            : Number(formFields.tradePricingValue || 0);

    const perYardFieldLabel =
        pricingMode === "markup" ? "Cost per yard" : "Price per yard";
    const perFabricFieldLabel =
        pricingMode === "markup"
            ? "Cost per fabric per item"
            : "Price per fabric per item";

    const salesDescriptionCom = formFields.salesDescription || "";
    const salesDescriptionWorkroom = formFields.salesDescriptionWorkroom || "";
    const orderingDescription = formFields.orderingDescription || "";

    return (
        <div className="space-y-6">
            <Section title="Information">
                <div className="space-y-1">
                    <Label className="text-[14px] font-medium text-[#111827]">
                        Category <span className="text-[#DC2626]">*</span>
                    </Label>
                    <SearchSelect
                        id="itemCategory"
                        searchType="modal"
                        searchItem="category"
                        searchPage="myshop"
                        onSearch={handleCategorySearch}
                        onItemClick={(id, name) => handleRootCategorySelect(id, name)}
                        onKeywordCleared={() => handleRootCategorySelect(0, "")}
                        initialValue={selectedRootCategory?.name || ""}
                        placeholder="Select category"
                        disabled={lockPrefill || lockCategory || !canEditMyShop}
                        setClientInfo={() => undefined}
                    />
                    {validationErrors.category && (
                        <ErrorText>Category is required.</ErrorText>
                    )}
                </div>

                {shouldShowSubcategory && (
                    <div className="space-y-1">
                        <Label className="text-[14px] font-medium text-[#111827]">
                            Subcategory
                        </Label>
                        <SearchSelect
                            id="itemSubcategory"
                            searchType="modal"
                            searchItem="category"
                            searchPage="myshop"
                            onSearch={handleSubcategorySearch}
                            onItemClick={(id, name) => handleSubcategorySelect(id, name)}
                            onKeywordCleared={() => handleSubcategorySelect(0, "")}
                            initialValue={selectedSubcategory?.name || ""}
                            placeholder="Select subcategory"
                            setClientInfo={() => undefined}
                            disabled={!canEditMyShop || (prefill?.subcategoryId && lockPrefill)}
                        />
                    </div>
                )}

                <div className="space-y-1">
                    <Label className="text-[14px] font-medium text-[#111827]">
                        Supplier {noSupplierRequired ? null : <span className="text-[#DC2626]">*</span>}
                    </Label>
                    <SearchSelect
                        id="itemSupplier"
                        searchType="modal"
                        searchItem="supplier"
                        searchPage="myshop"
                        onSearch={handleSupplierSearch}
                        onItemClick={(id, name) => handleSupplierSelect(id, name)}
                        onKeywordCleared={() => handleSupplierSelect(0, "")}
                        openModalClick={onOpenSupplierModal}
                        initialValue={formFields.supplierName || ""}
                        placeholder="Supplier"
                        setClientInfo={() => undefined}
                        disabled={!canEditMyShop || lockPrefill}
                    />
                    {validationErrors.supplier && (
                        <ErrorText>Supplier is required.</ErrorText>
                    )}
                </div>

                <div className="space-y-1">
                    <Label
                        htmlFor="my-shop-item-name"
                        className="text-[14px] font-medium text-[#111827]"
                    >
                        Name <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Input
                        id="my-shop-item-name"
                        value={formFields.name || ""}
                        placeholder="Enter name"
                        className={fieldClassName}
                        onChange={(event) => {
                            clearValidationError("name");
                            handleTextFieldChange("name", event.target.value);
                        }}
                        disabled={!canEditMyShop}
                    />
                    {validationErrors.name && <ErrorText>Name is required.</ErrorText>}
                    {submitErrorMessage && <ErrorText>{submitErrorMessage}</ErrorText>}
                </div>

                <div className="pt-1">
                    <Checkbox
                        disabled={!canEditMyShop}
                        uiDisabled={!canEditMyShop}
                        checked={showCustomerFacingName}
                        onClick={() => {
                            setShowCustomerFacingName((current) => {
                                const nextValue = !current;
                                if (!nextValue) {
                                    handleTextFieldChange("customerFacingName", "");
                                }
                                return nextValue;
                            });
                        }}
                        className="font-normal text-[14px]"
                    >
                        Add Customer-Facing Name
                    </Checkbox>
                </div>

                {showCustomerFacingName && (
                    <div className="space-y-1">
                        <Label
                            htmlFor="my-shop-customer-facing-name"
                            className="text-[14px] font-medium text-[#111827]"
                        >
                            Customer-Facing Name
                        </Label>
                        <Input
                            id="my-shop-customer-facing-name"
                            value={formFields.customerFacingName || ""}
                            placeholder="Enter customer-facing name"
                            className={fieldClassName}
                            onChange={(event) =>
                                handleTextFieldChange(
                                    "customerFacingName",
                                    event.target.value
                                )
                            }
                            disabled={!canEditMyShop}
                        />
                    </div>
                )}

                <div className="space-y-1">
                    <Label
                        htmlFor="my-shop-item-number"
                        className="text-[14px] font-medium text-[#111827]"
                    >
                        Item #
                    </Label>
                    <Input
                        id="my-shop-item-number"
                        value={formFields.itemNumber || ""}
                        placeholder="e.g., K-12345"
                        className={fieldClassName}
                        onChange={(event) =>
                            handleTextFieldChange("itemNumber", event.target.value)
                        }
                        disabled={!canEditMyShop}
                    />
                </div>

                <div className="space-y-1">
                    <Label
                        htmlFor="my-shop-item-notes"
                        className="text-[14px] font-medium text-[#111827]"
                    >
                        Internal Note
                    </Label>
                    <Textarea
                        id="my-shop-item-notes"
                        value={formFields.notes || ""}
                        placeholder="e.g. ordering minimums, special handling instructions, or vendor requirements"
                        className="min-h-[96px] rounded-md border border-input bg-background text-[14px] font-normal text-[#111827] placeholder:text-[#94A3B8]"
                        onChange={(event) =>
                            handleTextFieldChange("notes", event.target.value)
                        }
                        disabled={!canEditMyShop}
                    />
                </div>

                <div className="space-y-1">
                    <Label
                        htmlFor="my-shop-item-url"
                        className="text-[14px] font-medium text-[#111827]"
                    >
                        URL / Link
                    </Label>
                    <Input
                        id="my-shop-item-url"
                        value={formFields.url || ""}
                        placeholder="https://www.example.com/product"
                        className={fieldClassName}
                        onChange={(event) => handleTextFieldChange("url", event.target.value)}
                        disabled={!canEditMyShop}
                    />
                </div>

                {isEditMode && (
                    <div className="pt-1">
                        <Checkbox
                            checked={!formFields.isActive}
                            onClick={() =>
                                handleTextFieldChange("isActive", !formFields.isActive)
                            }
                            className="font-normal text-[14px]"
                            disabled={!canEditMyShop}
                            uiDisabled={!canEditMyShop}
                        >
                            Make Inactive
                        </Checkbox>
                    </div>
                )}
            </Section>

            {isFabricsOrLiningsCategory && (
                <Section title="Specifications">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div className="space-y-1">
                            <Label className="text-[14px] font-medium text-[#111827]">
                                Fabric Width
                            </Label>
                            <div className="relative">
                                <Input
                                    value={toInputValue(
                                        fabricsOrLiningsItemDetailsFields.fabricWidthInches
                                    )}
                                    placeholder="54"
                                    className={`${fieldClassName} pr-10`}
                                    onChange={handleSpecificationChange(
                                        "fabricWidthInches"
                                    )}
                                    onBlur={handleSpecificationBlur(
                                        "fabricWidthInches"
                                    )}
                                    disabled={!canEditMyShop}
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#64748B]">
                                    in
                                </span>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label className="text-[14px] font-medium text-[#111827]">
                                Vertical Repeat
                            </Label>
                            <div className="relative">
                                <Input
                                    value={toInputValue(
                                        fabricsOrLiningsItemDetailsFields.verticalRepeatInches
                                    )}
                                    placeholder="0"
                                    className={`${fieldClassName} pr-10`}
                                    onChange={handleSpecificationChange(
                                        "verticalRepeatInches"
                                    )}
                                    onBlur={handleSpecificationBlur(
                                        "verticalRepeatInches"
                                    )}
                                    disabled={!canEditMyShop}
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#64748B]">
                                    in
                                </span>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label className="text-[14px] font-medium text-[#111827]">
                                Horizontal Repeat
                            </Label>
                            <div className="relative">
                                <Input
                                    value={toInputValue(
                                        fabricsOrLiningsItemDetailsFields.horizontalRepeatInches
                                    )}
                                    placeholder="0"
                                    className={`${fieldClassName} pr-10`}
                                    onChange={handleSpecificationChange(
                                        "horizontalRepeatInches"
                                    )}
                                    onBlur={handleSpecificationBlur(
                                        "horizontalRepeatInches"
                                    )}
                                    disabled={!canEditMyShop}
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#64748B]">
                                    in
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-1">
                        <Checkbox
                            checked={!!fabricsOrLiningsItemDetailsFields.isDropRepeatOn}
                            onClick={toggleSpecificationCheckbox("isDropRepeatOn")}
                            className="font-normal text-[14px]"
                            disabled={!canEditMyShop}
                            uiDisabled={!canEditMyShop}
                        >
                            Drop Repeat
                        </Checkbox>
                        <Checkbox
                            checked={
                                !!fabricsOrLiningsItemDetailsFields.isSpecialtyFabricHandlingOn
                            }
                            onClick={toggleSpecificationCheckbox(
                                "isSpecialtyFabricHandlingOn"
                            )}
                            className="font-normal text-[14px]"
                            disabled={!canEditMyShop}
                            uiDisabled={!canEditMyShop}
                        >
                            Specialty Fabric Handling
                        </Checkbox>
                    </div>
                </Section>
            )}

            {canEditMyShop && (
              <Section title="Pricing">
                  {(readOnlyPricing || !canEditMyShop) && (
                      <div className="flex items-start gap-2 rounded-md border border-[#D8E0E8] bg-white px-3 py-3 text-[13px] text-[#60778F]">
                          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#3B97B1]" />
                          <span>These fields are editable only within My Shop.</span>
                      </div>
                  )}

                  <div className="space-y-2">
                      <Label className="text-[14px] font-medium text-[#111827]">
                          Method
                      </Label>
                      <div className="flex flex-wrap gap-6">
                          <label className="flex items-center gap-2 text-[14px] text-[#111827]">
                              <input
                                  type="radio"
                                  checked={pricingMode === "markup"}
                                  onChange={() => handlePricingModeChange("markup")}
                                  disabled={readOnlyPricing || !canEditMyShop}
                              />
                              Markup Cost
                          </label>
                          <label className="flex items-center gap-2 text-[14px] text-[#111827]">
                              <input
                                  type="radio"
                                  checked={pricingMode === "specify"}
                                  onChange={() => handlePricingModeChange("specify")}
                                  disabled={readOnlyPricing || !canEditMyShop}
                              />
                              Specify Price
                          </label>
                      </div>
                  </div>

                  {pricingMode === "markup" ? (
                      <div className="space-y-4">
                          <div className="max-w-[220px] space-y-1">
                              <Label className="text-[14px] font-medium text-[#111827]">
                                  Cost
                              </Label>
                              <div className="relative">
                                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]">
                                      $
                                  </span>
                                  <Input
                                      id="my-shop-item-cost"
                                      data-sheet-focus-field="cost"
                                      value={String(formFields.cost || "")}
                                      placeholder="0.00"
                                      className={`${fieldClassName} pl-7 text-right placeholder:text-right`}
                                      disabled={readOnlyPricing || !canEditMyShop}
                                      onChange={handlePricingFieldChange("cost")}
                                      onBlur={handlePricingFieldBlur("cost")}
                                  />
                              </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="space-y-1">
                                  <Label className="text-[14px] font-medium text-[#111827]">
                                      Retail Markup
                                  </Label>
                                  <div className="relative">
                                      <Input
                                          id="my-shop-item-retail-pricing"
                                          data-sheet-focus-field="retailPricingValue"
                                          value={String(formFields.retailPricingValue || "")}
                                          placeholder="0.00"
                                          className={`${fieldClassName} pr-8 text-right placeholder:text-right`}
                                          disabled={readOnlyPricing || !canEditMyShop}
                                          onChange={handlePricingFieldChange(
                                              "retailPricingValue"
                                          )}
                                          onBlur={handlePricingFieldBlur(
                                              "retailPricingValue"
                                          )}
                                      />
                                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]">
                                          %
                                      </span>
                                  </div>
                              </div>

                              <div className="space-y-1">
                                  <Label className="text-[14px] font-medium text-[#111827]">
                                      Trade Markup
                                  </Label>
                                  <div className="relative">
                                      <Input
                                          id="my-shop-item-trade-pricing"
                                          data-sheet-focus-field="tradePricingValue"
                                          value={String(formFields.tradePricingValue || "")}
                                          placeholder="0.00"
                                          className={`${fieldClassName} pr-8 text-right placeholder:text-right`}
                                          disabled={readOnlyPricing || !canEditMyShop}
                                          onChange={handlePricingFieldChange(
                                              "tradePricingValue"
                                          )}
                                          onBlur={handlePricingFieldBlur(
                                              "tradePricingValue"
                                          )}
                                      />
                                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]">
                                          %
                                      </span>
                                  </div>
                              </div>
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <div className="rounded-md bg-white px-4 py-3">
                                  <div className="text-[14px] font-medium text-[#111827]">
                                      Retail Price
                                  </div>
                                  <div className="mt-2 text-[18px] font-medium text-[#111827]">
                                      {formatCurrency(computedRetailPrice)}
                                  </div>
                              </div>
                              <div className="rounded-md bg-white px-4 py-3">
                                  <div className="text-[14px] font-medium text-[#111827]">
                                      Trade Price
                                  </div>
                                  <div className="mt-2 text-[18px] font-medium text-[#111827]">
                                      {formatCurrency(computedTradePrice)}
                                  </div>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                              <Label className="text-[14px] font-medium text-[#111827]">
                                  Retail Price
                              </Label>
                              <div className="relative">
                                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]">
                                      $
                                  </span>
                                  <Input
                                      id="my-shop-item-retail-pricing"
                                      data-sheet-focus-field="retailPricingValue"
                                      value={String(formFields.retailPricingValue || "")}
                                      placeholder="0.00"
                                      className={`${fieldClassName} pl-7 text-right placeholder:text-right`}
                                      disabled={readOnlyPricing || !canEditMyShop}
                                      onChange={handlePricingFieldChange(
                                          "retailPricingValue"
                                      )}
                                      onBlur={handlePricingFieldBlur(
                                          "retailPricingValue"
                                      )}
                                  />
                              </div>
                          </div>
                          <div className="space-y-1">
                              <Label className="text-[14px] font-medium text-[#111827]">
                                  Trade Price
                              </Label>
                              <div className="relative">
                                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#64748B]">
                                      $
                                  </span>
                                  <Input
                                      id="my-shop-item-trade-pricing"
                                      data-sheet-focus-field="tradePricingValue"
                                      value={String(formFields.tradePricingValue || "")}
                                      placeholder="0.00"
                                      className={`${fieldClassName} pl-7 text-right placeholder:text-right`}
                                      disabled={readOnlyPricing || !canEditMyShop}
                                      onChange={handlePricingFieldChange(
                                          "tradePricingValue"
                                      )}
                                      onBlur={handlePricingFieldBlur(
                                          "tradePricingValue"
                                      )}
                                  />
                              </div>
                          </div>
                      </div>
                  )}

                  {isFabricsOrLiningsCategory && (
                      <div className="space-y-4 rounded-md border border-[#D8E0E8] bg-white px-4 py-4">
                          <Checkbox
                              checked={shippingHandlingEnabled}
                              onClick={handleShippingToggle}
                              className="font-normal text-[14px]"
                              disabled={readOnlyPricing || !canEditMyShop}
                              uiDisabled={readOnlyPricing || !canEditMyShop}
                          >
                              Include Shipping & Handling
                          </Checkbox>

                          {shippingHandlingEnabled && (
                              <div className="space-y-3">
                                  <div className="flex items-center gap-3 pl-6">
                                      <label
                                          className={`flex items-center gap-2 text-[14px] ${
                                              shippingHandlingMode === "per-yard"
                                                  ? "text-[#111827]"
                                                  : "text-[#94A3B8]"
                                          }`}
                                      >
                                          <input
                                              type="radio"
                                              className="h-4 w-4 accent-[#3B97B1]"
                                              checked={shippingHandlingMode === "per-yard"}
                                              onChange={() =>
                                                  handleShippingModeChange("per-yard")
                                              }
                                              disabled={readOnlyPricing || !canEditMyShop}
                                          />
                                          {perYardFieldLabel}
                                      </label>
                                      <div className="relative w-[96px]">
                                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#94A3B8]">
                                              $
                                          </span>
                                          <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              placeholder="0.00"
                                              value={
                                                  shippingHandlingMode === "per-yard"
                                                      ? String(formFields.shippingCost || "")
                                                      : ""
                                              }
                                              className={`sih-number-input ${fieldClassName} h-10 w-[96px] pl-7 pr-2 ${
                                                  shippingHandlingMode !== "per-yard"
                                                      ? "bg-[#F8FAFC] text-[#94A3B8]"
                                                      : ""
                                              }`}
                                              disabled={
                                                  readOnlyPricing ||
                                                  shippingHandlingMode !== "per-yard" || !canEditMyShop
                                              }
                                              onChange={handlePricingFieldChange("shippingCost")}
                                              onBlur={handlePricingFieldBlur("shippingCost")}
                                          />
                                      </div>
                                  </div>

                                  <div className="flex items-center gap-3 pl-6">
                                      <label
                                          className={`flex items-center gap-2 text-[14px] ${
                                              shippingHandlingMode === "per-item"
                                                  ? "text-[#111827]"
                                                  : "text-[#94A3B8]"
                                          }`}
                                      >
                                          <input
                                              type="radio"
                                              className="h-4 w-4 accent-[#3B97B1]"
                                              checked={shippingHandlingMode === "per-item"}
                                              onChange={() =>
                                                  handleShippingModeChange("per-item")
                                              }
                                              disabled={readOnlyPricing || !canEditMyShop}
                                          />
                                          {perFabricFieldLabel}
                                      </label>
                                      <div className="relative w-[96px]">
                                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-[#94A3B8]">
                                              $
                                          </span>
                                          <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              placeholder="0.00"
                                              value={
                                                  shippingHandlingMode === "per-item"
                                                      ? String(formFields.shippingCost || "")
                                                      : ""
                                              }
                                              className={`sih-number-input ${fieldClassName} h-10 w-[96px] pl-7 pr-2 ${
                                                  shippingHandlingMode !== "per-item"
                                                      ? "bg-[#F8FAFC] text-[#94A3B8]"
                                                      : ""
                                              }`}
                                              disabled={
                                                  readOnlyPricing ||
                                                  shippingHandlingMode !== "per-item" || !canEditMyShop
                                              }
                                              onChange={handlePricingFieldChange("shippingCost")}
                                              onBlur={handlePricingFieldBlur("shippingCost")}
                                          />
                                      </div>
                                  </div>
                              </div>
                          )}
                      </div>
                  )}
              </Section>
            )}

            <Section title="Image">
                {formFields.avatarId ? (
                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-start">
                        <img
                            src={getImageSrc(formFields.avatarId)}
                            alt={formFields.name || "My Shop item"}
                            className="h-32 w-32 rounded-md border border-[#D8E0E8] object-cover"
                        />
                        <div className="flex flex-col items-center gap-4 pt-0.5">
                            {canEditMyShop && (
                              <Button
                                  type="button"
                                  variant="outline"
                                  className="min-w-[144px] justify-center gap-2 text-[14px] font-medium"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={isUploadingImage || !canEditMyShop}
                              >
                                  <Upload className="h-4 w-4" />
                                  Replace
                              </Button>
                            )}
                            <a
                                href={getImageSrc(formFields.avatarId)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex min-w-[144px] items-center justify-center gap-2 text-[14px] font-medium text-[#60778F] no-underline transition-colors hover:text-[#111827]"
                            >
                                <Eye className="h-4 w-4" />
                                View Full Size
                            </a>
                            {canEditMyShop && (
                              <button
                                  disabled={!canEditMyShop}
                                  type="button"
                                  className="inline-flex min-w-[144px] items-center justify-center gap-2 bg-transparent p-0 text-[14px] font-medium text-[#60778F] transition-colors hover:text-[#DC2626]"
                                  onClick={() =>
                                      dispatch({
                                          type: "updateFormFields",
                                          formFieldUpdates: { avatarId: null },
                                      })
                                  }
                              >
                                  <Trash2 className="h-4 w-4" />
                                  Remove
                              </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <label
                        className="flex cursor-pointer flex-col items-center justify-center rounded-[10px] border border-dashed border-[#D8E0E8] bg-white px-6 py-10 text-center"
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF6F9] text-[#60778F]">
                            {isUploadingImage ? (
                                <ImageIcon className="h-6 w-6" />
                            ) : (
                                <Upload className="h-6 w-6" />
                            )}
                        </div>
                        <div className="mt-4 text-[15px] text-[#60778F]">
                            Drag and drop files here or click to browse
                        </div>
                        <div className="mt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={(event) => {
                                    event.preventDefault();
                                    fileInputRef.current?.click();
                                }}
                                disabled={isUploadingImage || !canEditMyShop}
                            >
                                {isUploadingImage ? "Uploading..." : "Select File"}
                            </Button>
                        </div>
                    </label>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileChange}
                />
                {imageUploadError && (
                    <UploadErrorBanner
                        centered
                        message={imageUploadError}
                        style={{ marginTop: "16px" }}
                    />
                )}
            </Section>

            <Section title="Description Preview">
                <div className="text-[14px] text-[#60778F]">
                    Previews are based on your{" "}
                    <Link
                        to="/account_settings?tab=description_settings"
                        onClick={(event) => {
                            if (!onDescriptionSettingsClick) {
                                return;
                            }

                            event.preventDefault();
                            onDescriptionSettingsClick();
                        }}
                        className="text-[#2D8CA5] underline"
                    >
                        Description Settings
                    </Link>{" "}
                    and current values.
                </div>
                <div className="space-y-4 rounded-md bg-white px-4 py-4">
                    <PreviewBlock
                        label="Ordering Description"
                        value={orderingDescription}
                    />
                    <PreviewBlock
                        label="Sales Description — COM"
                        value={salesDescriptionCom}
                    />
                    <PreviewBlock
                        label="Sales Description — Workroom-Provided"
                        value={salesDescriptionWorkroom}
                    />
                </div>
            </Section>

            {(validationErrors.category ||
                validationErrors.supplier ||
                validationErrors.name) && (
                <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#991B1B]">
                    Review the required fields above before saving.
                </div>
            )}
        </div>
    );
};

export default MyShopItemWideSheetContent;
